/**
 * Passive Wander Behavior
 * 
 * NPC wanders randomly within configured bounds, ignores player.
 * Good for background NPCs and ambient characters.
 */

import type { NPCBehavior, NPCBehaviorConfig } from './NPCBehavior';
import { wanderTick, createWanderState, type WanderState } from '../presets/movement';
import { passiveDetect, defaultDeath } from '../presets/combat';
import type { AITickAPI } from '../hooks/NPCHookAPI';

interface BoundedWanderState extends WanderState {
  minT: number;
  maxT: number;
}

/**
 * Bounded wander tick - wanders randomly but stays within T bounds
 */
const boundedWanderTick = (api: AITickAPI, state: BoundedWanderState): void => {
  // Check if at bounds - reverse if needed
  if (api.npcT >= state.maxT && state.direction > 0) {
    state.direction = -1;
    state.timer = 0; // Force immediate direction update
  } else if (api.npcT <= state.minT && state.direction < 0) {
    state.direction = 1;
    state.timer = 0;
  }
  
  // Normal wander behavior within bounds
  wanderTick(api, state);
};

/**
 * Create a passive wander behavior instance.
 * 
 * Config options:
 * - patrolLeft: left bound distance from spawn (meters)
 * - patrolRight: right bound distance from spawn (meters)
 */
export const createPassiveWander = (config: NPCBehaviorConfig): NPCBehavior => {
  // State for this behavior instance (closures)
  let wanderState: BoundedWanderState | null = null;
  let spawnT = 0.5;
  let splineLength = 1;
  
  return {
    id: 'passive_wander',
    displayName: 'Passive Wanderer',
    
    onSpawn: (api) => {
      // Capture spawn position and spline length
      spawnT = api.spawnT;
      splineLength = api.splineLength;
      
      // Calculate bounds based on config
      const leftDistance = config.patrolLeft ?? 5;
      const rightDistance = config.patrolRight ?? 5;
      const leftT = leftDistance / splineLength;
      const rightT = rightDistance / splineLength;
      
      // Create wander state with bounds
      const baseState = createWanderState(1.5, 4.0); // Random wait between 1.5-4s
      wanderState = {
        ...baseState,
        minT: Math.max(0, spawnT - leftT),
        maxT: Math.min(1, spawnT + rightT),
      };
    },
    
    onTick: (api) => {
      if (!wanderState) {
        // Fallback init if onSpawn wasn't called
        wanderState = {
          ...createWanderState(),
          minT: 0,
          maxT: 1,
        };
      }
      
      boundedWanderTick(api, wanderState);
    },
    
    onPlayerDetected: passiveDetect,
    
    onDeath: defaultDeath,
  };
};
